import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Folder, FolderPlus, ChevronRight, ChevronDown, Pencil, Trash2,
  Plus, X, Inbox, Layers, Building2, User as UserIcon,
} from "lucide-react";

const ICON_MAP = { folder: Folder, building: Building2, user: UserIcon };

export default function FolderSidebar({
  folders,
  selectedId,
  onSelect,
  onChanged,
  className = "",
}) {
  const [expanded, setExpanded] = useState({}); // { folder_id: true }
  const [adding, setAdding] = useState(null); // parent_id (or "ROOT") if creating new
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(null); // folder_id
  const [renameText, setRenameText] = useState("");

  const tree = useMemo(() => {
    const byParent = {};
    for (const f of folders) {
      const p = f.parent_id || "ROOT";
      if (!byParent[p]) byParent[p] = [];
      byParent[p].push(f);
    }
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name, "fi")));
    return byParent;
  }, [folders]);

  // Auto-expand path to selected folder
  useEffect(() => {
    if (!selectedId) return;
    const byId = Object.fromEntries(folders.map((f) => [f.folder_id, f]));
    const open = { ...expanded };
    let cur = byId[selectedId];
    while (cur?.parent_id) {
      open[cur.parent_id] = true;
      cur = byId[cur.parent_id];
    }
    setExpanded(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, folders.length]);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/folders", { name, parent_id: adding === "ROOT" ? null : adding });
      setNewName("");
      setAdding(null);
      toast.success("Kansio luotu");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Luonti epäonnistui");
    }
  };

  const submitRename = async (folder_id) => {
    const name = renameText.trim();
    if (!name) return;
    try {
      await api.patch(`/folders/${folder_id}`, { name });
      setRenaming(null);
      setRenameText("");
      onChanged?.();
    } catch {
      toast.error("Nimeäminen epäonnistui");
    }
  };

  const remove = async (folder_id) => {
    if (!confirm("Poista tämä kansio ja kaikki sen alikansiot? Raportit ja tiedotteet siirtyvät 'Hallinnoimaton'-listalle.")) return;
    try {
      await api.delete(`/folders/${folder_id}`);
      if (selectedId === folder_id) onSelect?.(null);
      onChanged?.();
      toast.success("Kansio poistettu");
    } catch {
      toast.error("Poisto epäonnistui");
    }
  };

  const renderNode = (folder, depth) => {
    const children = tree[folder.folder_id] || [];
    const isOpen = !!expanded[folder.folder_id];
    const isSelected = selectedId === folder.folder_id;
    const isRenaming = renaming === folder.folder_id;
    const Icon = ICON_MAP[folder.icon] || Folder;
    return (
      <div key={folder.folder_id} className="select-none" data-testid={`folder-node-${folder.folder_id}`}>
        <div
          className={`group flex items-center gap-1 rounded-lg pr-1 ${
            isSelected ? "bg-emerald-50 text-emerald-900" : "hover:bg-slate-100 text-slate-700"
          }`}
          style={{ paddingLeft: depth * 12 }}
        >
          <button
            type="button"
            onClick={() => setExpanded((m) => ({ ...m, [folder.folder_id]: !isOpen }))}
            className="h-7 w-5 flex items-center justify-center text-slate-400 hover:text-slate-700"
            data-testid={`folder-toggle-${folder.folder_id}`}
            aria-label="toggle"
          >
            {children.length > 0 ? (
              isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <span className="h-3.5 w-3.5" />
            )}
          </button>
          <Icon className={`h-4 w-4 flex-shrink-0 ${isSelected ? "text-emerald-600" : "text-slate-400"}`} />
          {isRenaming ? (
            <input
              autoFocus
              value={renameText}
              onChange={(e) => setRenameText(e.target.value)}
              onBlur={() => submitRename(folder.folder_id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); submitRename(folder.folder_id); }
                if (e.key === "Escape") { setRenaming(null); setRenameText(""); }
              }}
              className="flex-1 min-w-0 px-1.5 py-1 text-sm rounded border border-emerald-300 outline-none"
              data-testid={`folder-rename-input-${folder.folder_id}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => onSelect?.(folder.folder_id)}
              className="flex-1 min-w-0 text-left text-sm py-1.5 truncate"
              data-testid={`folder-select-${folder.folder_id}`}
            >
              <span className="font-medium truncate">{folder.name}</span>
              {(folder.report_count > 0 || folder.notice_count > 0) && (
                <span className="ml-2 text-[10px] text-slate-400">
                  {(folder.report_count || 0) + (folder.notice_count || 0)}
                </span>
              )}
            </button>
          )}
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
            <button
              type="button"
              onClick={() => { setAdding(folder.folder_id); setNewName(""); setExpanded((m) => ({ ...m, [folder.folder_id]: true })); }}
              className="p-1 rounded hover:bg-slate-200 text-slate-500"
              data-testid={`folder-add-child-${folder.folder_id}`}
              title="Uusi alikansio"
            >
              <Plus className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => { setRenaming(folder.folder_id); setRenameText(folder.name); }}
              className="p-1 rounded hover:bg-slate-200 text-slate-500"
              data-testid={`folder-rename-${folder.folder_id}`}
              title="Nimeä uudelleen"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => remove(folder.folder_id)}
              className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
              data-testid={`folder-delete-${folder.folder_id}`}
              title="Poista"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>

        {adding === folder.folder_id && (
          <div className="flex items-center gap-1 mt-1 mb-1" style={{ paddingLeft: (depth + 1) * 12 + 24 }}>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); submitNew(); }
                if (e.key === "Escape") { setAdding(null); setNewName(""); }
              }}
              placeholder="Alikansion nimi"
              className="flex-1 px-2 py-1 text-sm rounded border border-emerald-300 outline-none"
              data-testid="folder-new-input"
            />
            <button onClick={submitNew} className="p-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600" data-testid="folder-new-submit">
              <Plus className="h-3 w-3" />
            </button>
            <button onClick={() => { setAdding(null); setNewName(""); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {isOpen && children.length > 0 && (
          <div>{children.map((c) => renderNode(c, depth + 1))}</div>
        )}
      </div>
    );
  };

  const roots = tree.ROOT || [];

  return (
    <aside className={`card-soft p-3 ${className}`} data-testid="folder-sidebar">
      <div className="flex items-center justify-between px-2 pt-1 pb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" /> Kansiot
        </div>
        <button
          type="button"
          onClick={() => { setAdding("ROOT"); setNewName(""); }}
          className="p-1 rounded hover:bg-slate-100 text-slate-500"
          data-testid="folder-add-root"
          title="Uusi yläkansio"
        >
          <FolderPlus className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-0.5">
        <button
          type="button"
          onClick={() => onSelect?.("ALL")}
          className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${
            selectedId === "ALL" || selectedId === null || selectedId === undefined
              ? "bg-emerald-50 text-emerald-900 font-semibold"
              : "hover:bg-slate-100 text-slate-700"
          }`}
          data-testid="folder-select-all"
        >
          <Layers className="h-4 w-4 text-slate-400" /> Kaikki
        </button>
        <button
          type="button"
          onClick={() => onSelect?.("UNFILED")}
          className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${
            selectedId === "UNFILED" ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-slate-100 text-slate-700"
          }`}
          data-testid="folder-select-unfiled"
        >
          <Inbox className="h-4 w-4 text-slate-400" /> Hallinnoimaton
        </button>
      </div>

      {adding === "ROOT" && (
        <div className="flex items-center gap-1 mt-2">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); submitNew(); }
              if (e.key === "Escape") { setAdding(null); setNewName(""); }
            }}
            placeholder="esim. As Oy Kirjokuja"
            className="flex-1 px-2 py-1 text-sm rounded border border-emerald-300 outline-none"
            data-testid="folder-new-input"
          />
          <button onClick={submitNew} className="p-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600" data-testid="folder-new-submit">
            <Plus className="h-3 w-3" />
          </button>
          <button onClick={() => { setAdding(null); setNewName(""); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="mt-2 border-t border-slate-100 pt-2">
        {roots.length === 0 && adding !== "ROOT" ? (
          <button
            type="button"
            onClick={() => { setAdding("ROOT"); setNewName(""); }}
            className="w-full text-center text-xs text-slate-500 hover:text-emerald-600 py-3 rounded-lg border border-dashed border-slate-200 hover:border-emerald-300 transition-colors"
            data-testid="folder-empty-cta"
          >
            <FolderPlus className="h-4 w-4 inline mr-1" /> Luo ensimmäinen asiakas/kansio
          </button>
        ) : (
          roots.map((f) => renderNode(f, 0))
        )}
      </div>
    </aside>
  );
}

// Helper: build breadcrumb from selectedId
export function folderBreadcrumb(folders, selectedId) {
  if (!selectedId || selectedId === "ALL") return [{ id: "ALL", name: "Kaikki" }];
  if (selectedId === "UNFILED") return [{ id: "UNFILED", name: "Hallinnoimaton" }];
  const byId = Object.fromEntries(folders.map((f) => [f.folder_id, f]));
  const path = [];
  let cur = byId[selectedId];
  while (cur) {
    path.unshift({ id: cur.folder_id, name: cur.name });
    cur = cur.parent_id ? byId[cur.parent_id] : null;
  }
  return path;
}
